<template>
  <VCol cols="12">
    <SettingsCard
      :title="$t('title')"
      :subtitle="$t('subtitle')"
    >
      <template #tooltip>
        <div class="tooltip__content-example">
          <div class="tooltip__content-text-wrapper">
            <div class="tooltip__content-text">
              {{ $t('tooltip.1') }}
            </div>
            <div class="tooltip__content-text">
              {{ $t('tooltip.2') }}
            </div>
            <div class="tooltip__content-text">
              {{ $t('tooltip.3') }}
            </div>
          </div>
          <div class="tooltip__content-example-block">
            <div class="tooltip__content-text-example">
              {{ $t('example') }}:
            </div>
            <img
              :src="require(`@/assets/img/account/settings/${tooltipImg}`)"
              alt="info"
            />
          </div>
        </div>
      </template>

          <div class="account-settings__fields-rows">
            <AccountSettingsInterviewFormRow
              v-for="(field, index) in additionalFieldsArray"
              :key="index"
              :field="field"
              :all-fields="additionalFieldsArray"
              :index="index"
              :show-validation-messages-global="showValidationMessagesGlobal"
              @removeField="removeField"
            />
          </div>
          <div @click="addField" class="add-field-btn">
            <VIcon>mdi-plus</VIcon>
            {{ $t('addField') }}
          </div>
          <div class="account-settings__actions">
            <VBtn
              color="success"
              @click="postAddFields"
              :disabled="disabledSaveButton"
            >
              {{ $t('save') }}
            </VBtn>
            <VBtn
              color="error"
              @click="showDeleteConfirmation = true"
              :disabled="additionalFieldsArrayIsEmpty"
            >
              {{ $t('deleteAllFields') }}
            </VBtn>
          </div>
    </SettingsCard>

    <VDialog v-model="showDeleteConfirmation" max-width="500px">
      <VCard>
        <div class="delete-confirmation__wrap">
          <div class="delete-confirmation__wrap-text">
            {{ $t('deleteConfirmation.message') }}
          </div>

          <div class="delete-confirmation__wrap-actions">
            <VBtn
              color="error"
              variant="text"
              @click="confirmDelete"
            >
              {{ $t('deleteConfirmation.confirm') }}
            </VBtn>
            <VBtn
              variant="text"
              class="cancel-btn"
              @click="showDeleteConfirmation = false"
            >
              {{ $t('deleteConfirmation.cancel') }}
            </VBtn>
          </div>

          <VBtn
            icon
            small
            absolute
            top
            right
            class="delete-confirmation__close-btn"
            @click="showDeleteConfirmation = false"
          >
            <VIcon>mdi-close</VIcon>
          </VBtn>
        </div>
      </VCard>
    </VDialog>

    <VDialog v-model="showDeleteRowConfirmation" max-width="500px">
      <VCard>
        <div class="delete-confirmation__wrap">
          <div class="delete-confirmation__wrap-text">
            {{ $t('deleteRowConfirmation.message') }}
          </div>

          <div class="delete-confirmation__wrap-actions">
            <VBtn
              color="error"
              variant="text"
              @click="confirmDeleteRow"
            >
              {{ $t('deleteRowConfirmation.confirm') }}
            </VBtn>
            <VBtn
              variant="text"
              class="cancel-btn"
              @click="showDeleteRowConfirmation = false"
            >
              {{ $t('deleteRowConfirmation.cancel') }}
            </VBtn>
          </div>

          <VBtn
            icon
            small
            absolute
            top
            right
            class="delete-confirmation__close-btn"
            @click="showDeleteRowConfirmation = false"
          >
            <VIcon>mdi-close</VIcon>
          </VBtn>
        </div>
      </VCard>
    </VDialog>
  </VCol>
</template>

<script>
import {
  computed, ref, watch, toRefs, nextTick, getCurrentInstance,
} from '@vue/composition-api';
import cloneDeep from 'lodash.clonedeep';
import slugify from '@sindresorhus/slugify';
import isEqual from 'lodash.isequal';
import useCompany from '@/compositions/useCompany';
import SettingsCard from '@/components/Settings/SettingsCard.vue';
import { addFieldTypes } from '@/enums';
import { useI18n } from 'vue-i18n-composable';
import AccountSettingsInterviewFormRow from './AccountSettingsInterviewFormRow.vue';

export default {
  name: 'AccountSettingsInterviewForm',
  components: {
    SettingsCard,
    AccountSettingsInterviewFormRow,
  },
  props: {
    company: {
      type: Object,
      required: true,
    },
  },
  setup(props, { root }) {
    const { company } = toRefs(props);
    const { addCandidatesAdditionalFields } = useCompany();
    const { locale, t } = useI18n();
    const showDeleteConfirmation = ref(false);
    const showDeleteRowConfirmation = ref(false);
    const selectedRowIndex = ref(null);

    const showValidationMessagesGlobal = ref(false);
    const duplicateLabelError = ref(false);

    const candidateAdditionalFields = computed(() => company.value?.candidateAdditionalFields);
    const additionalFieldsArray = ref(cloneDeep(company.value?.candidateAdditionalFields) || []);

    const additionalFieldsArrayIsEmpty = computed(() => !additionalFieldsArray.value || additionalFieldsArray.value.length === 0);

    const tooltipImg = computed(() => (locale.value === 'ru' ? 'imgTooltipRu.png' : 'imgTooltipEn.jpg'));

    const getCleanFields = () => {
      return additionalFieldsArray.value.map(({ newValue, isValid, ...field }) => field);
    };

    const haveChanges = computed(() => {
      const cleanCurrentFields = getCleanFields();
      return isEqual(candidateAdditionalFields.value, cleanCurrentFields);
    });

    const disabledSaveButton = computed(() => haveChanges.value || additionalFieldsArrayIsEmpty.value);

    const hasInvalidFields = computed(() => additionalFieldsArray.value.filter((f) => !f.isValid).length > 0);

    const isValid = computed(() => !(hasInvalidFields.value || duplicateLabelError.value));

    const addField = () => {
      const newField = {
        type: addFieldTypes.STRING,
        label: '',
        value: null,
        required: true,
        isValid: true,
      };
      const instance = getCurrentInstance();
      if (instance && instance.proxy && instance.proxy.$set) {
        instance.proxy.$set(newField, 'availableValues', []);
      } else {
        newField.availableValues = [];
      }
      additionalFieldsArray.value = [...additionalFieldsArray.value, newField];
    };

    const validateFields = () => {
      const labelSet = new Set();
      duplicateLabelError.value = false;

      additionalFieldsArray.value.forEach((field) => {
        field.isValid = field.label.trim() !== '';

        if (field.type === 'select') {
          field.isValid = field.isValid && Array.isArray(field.availableValues) && field.availableValues.length > 0;
        }

        if (labelSet.has(field.label)) {
          duplicateLabelError.value = true;
        } else {
          labelSet.add(field.label);
        }
      });
    };

    const saveAddFields = () => {
      additionalFieldsArray.value = additionalFieldsArray.value.map((field) => ({
        ...field,
        value: field.value || slugify(field.label),
      }));
    };

    const postAddFields = async () => {
      validateFields();

      if (!isValid.value) {
        showValidationMessagesGlobal.value = true;
        nextTick(() => {
          showValidationMessagesGlobal.value = false;
        });
        return;
      }

      if (additionalFieldsArray.value.length) {
        saveAddFields();
      }

      const cleanFields = getCleanFields();

      await addCandidatesAdditionalFields({
        additionalFields: cleanFields,
      });

      await nextTick();
      await new Promise((resolve) => setTimeout(resolve, 200));
      const updatedFields = cloneDeep(company.value?.candidateAdditionalFields || []);
      additionalFieldsArray.value = updatedFields;

      root.$toast.success(t('saved'));
    };

    const removeField = (index) => {
      selectedRowIndex.value = index;
      showDeleteRowConfirmation.value = true;
    };

    const confirmDeleteRow = () => {
      additionalFieldsArray.value.splice(selectedRowIndex.value, 1);
      showDeleteRowConfirmation.value = false;
      selectedRowIndex.value = null;
    };

    const deleteAllFields = async () => {
      additionalFieldsArray.value = [];

      await postAddFields();
    };

    const confirmDelete = async () => {
      showDeleteConfirmation.value = false;
      await deleteAllFields();
    };

    watch(additionalFieldsArray, (fields) => {
      if (!Array.isArray(fields)) {
        additionalFieldsArray.value = [];
      } else {
        fields.forEach((field) => {
          if (field.type === 'select') {
            if (!field.availableValues || !Array.isArray(field.availableValues)) {
              field.availableValues = [];
            }
          } else if (field.type !== 'select') {
            if (field.availableValues) {
              delete field.availableValues;
            }
          }

          if (field.isValid === undefined) {
            field.isValid = true;
          }
        });
      }
    }, { deep: true });

    watch(candidateAdditionalFields, (newFields) => {
      if (newFields && !isEqual(newFields, additionalFieldsArray.value)) {
        const currentFields = cloneDeep(newFields);
        additionalFieldsArray.value = currentFields;
      }
    }, { deep: true });

    return {
      candidateAdditionalFields,
      additionalFieldsArray,
      addField,
      removeField,
      haveChanges,
      postAddFields,
      deleteAllFields,
      additionalFieldsArrayIsEmpty,
      showDeleteConfirmation,
      showDeleteRowConfirmation,
      confirmDelete,
      confirmDeleteRow,
      showValidationMessagesGlobal,
      duplicateLabelError,
      disabledSaveButton,
      tooltipImg,
    };
  },
};
</script>

<style lang="scss" scoped>
.account-settings {
  &__fields-rows {
    margin: 40px 0 10px;
    @media screen and (max-width: 768px) {
      margin: 20px 0 10px;
    }
  }
  &__actions {
    margin-top: 30px;
    button {
      margin-right: 10px;
    }
  }
}

.add-field-btn {
  cursor: pointer;
  width: fit-content;
}

.tooltip {
  &__content-example {
    display: flex;
    gap: 25px;
    @media screen and (max-width: 800px) {
      flex-direction: column;
    }
  }
  &__content-text-wrapper {
    width: 60%;
    @media screen and (max-width: 800px) {
      width: 100%;
    }
    > *:not(:last-child) {
      margin-bottom: 20px;
      @media screen and (max-width: 800px) {
        margin-bottom: 10px;
      }
    }
  }
  &__content-text-example {
    margin-bottom: 20px;
    @media screen and (max-width: 800px) {
      margin-bottom: 10px;
    }
  }
  &__content-example-block {
    img {
      width: 100%;
      height: auto;
      border-radius: 3px;
    }
  }
}

.delete-confirmation {
  &__wrap {
    padding: 30px;
    position: relative;
  }
  &__wrap-text {
    max-width: 330px;
    font-size: 16px;
    line-height: 20px;
    margin-bottom: 30px;
  }
  &__wrap-actions {
    display: flex;
    gap: 12px;
  }
  &__close-btn {
    position: absolute;
    top: 15px;
    right: 15px;
    cursor: pointer;
  }
}

.cancel-btn {
  background-color: #F3F3F3 !important;
  color: #212121 !important;
  &:hover {
    background-color: rgba(128, 128, 128, 0.1) !important;
  }
}

.error-message {
  color: #ff5252;
  margin-top: 10px;
  font-size: 14px;
}

::v-deep .custom-tooltip {
  max-width: 400px !important;
  background-color: #fff !important;
  color: rgba(0, 0, 0, 0.87) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;

  .v-card__text {
    padding: 16px !important;

    div {
      margin-bottom: 8px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}
</style>

<i18n locale="en" lang="yaml">
  title: Setting up the registration form
  subtitle: Setting up additional fields in the candidate registration form. You can add additional fields in different formats.
  addField: Add a field
  deleteAllFields: Delete all fields
  save: Save
  saved: Saved
  fieldsSaved: The fields are saved
  fieldsDeleted: All fields have been deleted
  example: Example
  tooltip: {
    1: 'When responding to a vacancy, the candidate fills out a registration form to participate in a video interview.',
    2: 'This form already provides fields for entering candidate data, such as full name, email,
    phone number, date of birth, and others.',
    3: 'If you need to get additional information about a candidate, you can add the appropriate fields to the registration form.
    You can also mark the fields as required.',
  }
  deleteConfirmation:
    title: Delete all fields
    message: Are you sure you want to delete all fields? This action cannot be undone.
    cancel: Cancel
    confirm: Delete
  deleteRowConfirmation:
    title: Delete field
    message: Are you sure you want to delete this field? This action cannot be undone.
    cancel: Cancel
    confirm: Delete
</i18n>

<i18n locale="ru" lang="yaml">
  title: Настройка формы регистрации
  subtitle: Настройка дополнительных полей в форму регистрации кандидата. Вы можете внести дополнительные поля разных форматов.
  addField: Добавить поле
  deleteAllFields: Удалить все поля
  save: Сохранить
  saved: Сохранено
  fieldsSaved: Поля сохранены
  fieldsDeleted: Все поля удалены
  example: Пример
  tooltip: {
    1: 'При отклике на вакансию кандидат заполняет регистрационную форму для участия в видео-интервью.',
    2: 'В данной форме уже предусмотрены поля для ввода данных кандидата, такие как ФИО, электронная почта,
    номер телефона, дата рождения и другие.',
    3: 'Если вам необходимо получить дополнительную информацию о кандидате, вы можете добавить соответствующие поля в форму регистрации.
    Также вы можете отметить поля как обязательные для заполнения.',
  }
  deleteConfirmation:
    title: Удалить все поля
    message: Вы уверены что хотите удалить все дополнительные поля?
    cancel: Отмена
    confirm: Удалить
  deleteRowConfirmation:
    title: Удалить поле
    message: Вы уверены, что хотите удалить дополнительное поле?
    cancel: Отмена
    confirm: Удалить
</i18n>
